import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ArrowLeft, BookOpen, Printer, Brain, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import AIPracticeScenario from '../shared/AIPracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function QuadruneEvolution() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };
  
  const [exercises, setExercises] = useState(() => {
    const saved = localStorage.getItem('quadruneEvolutionExercises');
    return saved ? JSON.parse(saved) : {
      lizard1: '', lizard2: '',
      furryMammal: '',
      monkeyMind: '',
      higherPorpoise: '',
      dangerImages: '', dangerThoughts: '', dangerSensations: '', dangerThreat: '', dangerResponse: '', dangerEffective: ''
    };
  });

  const saveProgress = () => {
    localStorage.setItem('quadruneEvolutionExercises', JSON.stringify(exercises));
    toast.success('Exercises saved!');
  };

  const reset = () => {
    if (window.confirm('Reset all exercises?')) {
      setExercises({ lizard1: '', lizard2: '', furryMammal: '', monkeyMind: '', higherPorpoise: '', dangerImages: '', dangerThoughts: '', dangerSensations: '', dangerThreat: '', dangerResponse: '', dangerEffective: '' });
      localStorage.removeItem('quadruneEvolutionExercises');
      toast.success('Reset!');
    }
  };

  useEffect(() => {
    const interval = setInterval(() => localStorage.setItem('quadruneEvolutionExercises', JSON.stringify(exercises)), 30000);
    return () => clearInterval(interval);
  }, [exercises]);

  const brainExercises = [
    {
      title: 'Lizard Legacy - REPTILIAN BRAIN',
      subtitle: 'Brain Stem and Cerebellum',
      description: 'Our physical instincts: Safety, sustenance, sex',
      exercise: 'What do your reptilian instincts want that helped your ancient ancestors survive and reproduce but that now have negative consequences if you act on them indiscriminately, habitually, or in ways that are out of integrity? (List everything related to food, substances, and sex that cause you problems or challenge one or more of your relationships.)',
      appreciation: 'What do you appreciate about your reptilian instincts? How do they serve your life and your relations?',
      color: 'from-green-900/30 to-emerald-900/30',
      borderColor: 'border-green-700/30',
      icon: '🦎'
    },
    {
      title: 'Furry Li\'l Mammal - OLD MAMMALIAN BRAIN',
      subtitle: 'Limbic System',
      description: 'Our social instincts: Bonding, status, and play',
      exercise: '"Soliciting Feedback" - See the weekly homework for this exercise',
      color: 'from-amber-900/30 to-orange-900/30',
      borderColor: 'border-amber-700/30',
      icon: '🦔'
    },
    {
      title: 'Monkey Mind - NEW MAMMALIAN BRAIN',
      subtitle: 'Neocortex',
      description: 'Our interpretive (often self-deceptive) instincts: Meaning, morality, and making sense',
      exercise: '"I notice, I imagine" - Look around you and practice saying what it is you are actually observing (something that can be photographed or recorded) and then saying what judgment or assessment you have about that thing or event.',
      color: 'from-blue-900/30 to-cyan-900/30',
      borderColor: 'border-blue-700/30',
      icon: '🐵'
    },
    {
      title: 'Higher Porpoise - ADVANCED',
      subtitle: 'Prefrontal Cortex',
      description: 'Our executive brain/co-creative instincts: Wholeness, transcendence, & contribution',
      exercise: 'Think of an event in your life that seemed like a "bad" thing at the time but later proved to be a "good" thing. Now think of something that you are afraid of and imagine how it could actually have a positive effect on your life.',
      deeperExercise: '"Is my life really in danger?" Think of a time when someone said or did something and you went into fight or flight. What images did you have? What thoughts? What sensations in your body? Was there anything actually life threatening? How did you respond? Would another response have been more effective?',
      color: 'from-purple-900/30 to-pink-900/30',
      borderColor: 'border-purple-700/30',
      icon: '🐬'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
              <div><h1 className="text-2xl font-bold text-white">The Quadrune Brain and Evolutionary Psychology</h1></div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={reset} variant="outline" size="sm" className="border-red-600 hover:bg-red-900/20 text-white">
                <RotateCcw className="h-4 w-4 mr-2" />Reset
              </Button>
              <Button onClick={saveProgress} variant="outline" size="sm" className="border-green-600 hover:bg-green-900/20 text-white">
                <Save className="h-4 w-4 mr-2" />Save
              </Button>
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white">
                <Printer className="h-4 w-4 mr-2" />Print
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm"><BookOpen className="h-4 w-4" /><span>Page 54</span></div>
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8"><Brain className="h-16 w-16 text-purple-400 mx-auto mb-4" /><h2 className="text-3xl font-bold text-white mb-4">Stone-Age Instincts in a Modern World</h2></div>
          <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-6 mb-8">
            <p className="text-amber-100 leading-relaxed mb-3">
              Our mind emerges from the interaction of four sequentially evolved components of our brain which evolved over millions of years. At the root of many of our life challenges, especially our close relationships, is the fact that we operate out of inherited "Stone-Age Instincts" in a world of "Supernormal Allurements."
            </p>
            <p className="text-amber-200 font-semibold">One of the most important lessons: be compassionate with ourselves and each other.</p>
          </div>
          <div className="space-y-6 mb-12">
            {brainExercises.map((brain, idx) => (
              <div key={idx} className={`bg-gradient-to-br ${brain.color} border ${brain.borderColor} rounded-lg overflow-hidden`}>
                <div className="bg-slate-900/40 px-6 py-4 border-b border-white/10">
                  <div className="flex items-center gap-4">
                    <div className="text-5xl">{brain.icon}</div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">{brain.title}</h3>
                      <p className="text-slate-300 italic text-sm">{brain.subtitle}</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-slate-100 text-lg mb-6">{brain.description}</p>
                  <div className="bg-slate-900/40 rounded-lg p-5 border border-white/10">
                    <p className="text-white font-semibold mb-3">Exercise:</p>
                    <p className="text-slate-200 leading-relaxed mb-4">{brain.exercise}</p>
                    
                    {/* Interactive Exercise Forms */}
                    {idx === 0 && (
                      <div className="space-y-4 mt-6">
                        <Textarea 
                          value={exercises.lizard1}
                          onChange={(e) => setExercises({...exercises, lizard1: e.target.value})}
                          placeholder="List instincts that helped ancestors but cause problems now (food, substances, sex)..."
                          className="bg-green-900/20 border-green-700/30 text-white placeholder:text-green-300/40 min-h-[100px]"
                        />
                        <p className="text-green-200 text-sm italic">Did you know 74.6% of Americans are overweight or obese?</p>
                        <Textarea 
                          value={exercises.lizard2}
                          onChange={(e) => setExercises({...exercises, lizard2: e.target.value})}
                          placeholder="What do you appreciate about your reptilian instincts? How do they serve you?"
                          className="bg-green-900/20 border-green-700/30 text-white placeholder:text-green-300/40 min-h-[80px]"
                        />
                      </div>
                    )}
                    
                    {idx === 1 && (
                      <div className="mt-6">
                        <Textarea 
                          value={exercises.furryMammal}
                          onChange={(e) => setExercises({...exercises, furryMammal: e.target.value})}
                          placeholder="Soliciting Feedback - See weekly homework..."
                          className="bg-amber-900/20 border-amber-700/30 text-white placeholder:text-amber-300/40 min-h-[80px]"
                        />
                      </div>
                    )}
                    
                    {idx === 2 && (
                      <div className="mt-6">
                        <Textarea 
                          value={exercises.monkeyMind}
                          onChange={(e) => setExercises({...exercises, monkeyMind: e.target.value})}
                          placeholder="Practice 'I notice, I imagine' - Write observations vs judgments..."
                          className="bg-blue-900/20 border-blue-700/30 text-white placeholder:text-blue-300/40 min-h-[100px]"
                        />
                      </div>
                    )}
                    
                    {idx === 3 && (
                      <div className="mt-6">
                        <Textarea 
                          value={exercises.higherPorpoise}
                          onChange={(e) => setExercises({...exercises, higherPorpoise: e.target.value})}
                          placeholder="Think of a 'bad' thing that became 'good'. Now imagine how something you fear could be positive..."
                          className="bg-purple-900/20 border-purple-700/30 text-white placeholder:text-purple-300/40 min-h-[100px]"
                        />
                      </div>
                    )}
                    
                    {brain.appreciation && (
                      <p className="text-slate-300 leading-relaxed mt-4 italic">{brain.appreciation}</p>
                    )}
                    {brain.deeperExercise && (
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <p className="text-slate-200 leading-relaxed">{brain.deeperExercise}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* "Is My Life Really in Danger?" Exercise */}
          <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-red-200 mb-4">Exercise: "Is My Life Really in Danger?"</h3>
            <p className="text-red-100 mb-6">Think of a time when someone said or did something and you went into fight or flight (e.g., your partner says "you're so selfish" or "I don't care what you want").</p>
            <div className="space-y-4">
              <div>
                <label className="text-red-300 text-sm mb-2 block">What images did you have in your mind?</label>
                <Textarea value={exercises.dangerImages} onChange={(e) => setExercises({...exercises, dangerImages: e.target.value})} placeholder="Describe the mental images..." className="bg-red-900/30 border-red-700/40 text-white min-h-[80px]" />
              </div>
              <div>
                <label className="text-red-300 text-sm mb-2 block">What thoughts did you have of the other person or yourself?</label>
                <Textarea value={exercises.dangerThoughts} onChange={(e) => setExercises({...exercises, dangerThoughts: e.target.value})} placeholder="Your thoughts..." className="bg-red-900/30 border-red-700/40 text-white min-h-[80px]" />
              </div>
              <div>
                <label className="text-red-300 text-sm mb-2 block">What sensations did you feel in your body?</label>
                <Textarea value={exercises.dangerSensations} onChange={(e) => setExercises({...exercises, dangerSensations: e.target.value})} placeholder="Physical sensations..." className="bg-red-900/30 border-red-700/40 text-white min-h-[80px]" />
              </div>
              <div>
                <label className="text-red-300 text-sm mb-2 block">Was there anything actually life threatening in that interaction?</label>
                <Textarea value={exercises.dangerThreat} onChange={(e) => setExercises({...exercises, dangerThreat: e.target.value})} placeholder="Your reflection..." className="bg-red-900/30 border-red-700/40 text-white min-h-[60px]" />
              </div>
              <div>
                <label className="text-red-300 text-sm mb-2 block">How did you respond and how did it affect the situation?</label>
                <Textarea value={exercises.dangerResponse} onChange={(e) => setExercises({...exercises, dangerResponse: e.target.value})} placeholder="Your response and impact..." className="bg-red-900/30 border-red-700/40 text-white min-h-[80px]" />
              </div>
              <div>
                <label className="text-red-300 text-sm mb-2 block">Would another response have been more effective in meeting everyone's needs?</label>
                <Textarea value={exercises.dangerEffective} onChange={(e) => setExercises({...exercises, dangerEffective: e.target.value})} placeholder="Alternative responses..." className="bg-red-900/30 border-red-700/40 text-white min-h-[80px]" />
              </div>
            </div>
          </div>

          <p className="text-center text-slate-400 text-sm mb-8">💾 Auto-saves every 30 seconds</p>

          <AIPracticeScenario pageTitle="The Quadrune Brain and Evolutionary Psychology" pageNumber={54} />
          <PageMessaging pageTitle="The Quadrune Brain and Evolutionary Psychology" pageNumber={54} />
        </div>
      </div>
    </div>
  );
}
